// src/chat/boardGatekeeper.ts
// -------------------------------------------------------
// Thin dispatcher — delegates all game-type-specific logic
// to the active GameProfile.
//
// Replaces the old hardcoded KCV_PROFILE + Phase type.
// -------------------------------------------------------

import type { GameProfile, GameState, BoardPayload } from '../config/gameProfiles/GameProfile';

// ── Board gatekeeper (answer-leak prevention) ────────────

/**
 * Hard safety layer: prevents answer leaks on the DigiBoard
 * during phases defined in the profile's `noAnswerPhases`.
 *
 * This is a generalized version of the old `gatekeepBoard()`.
 */
export function gatekeepBoard(
  profile: GameProfile,
  phase: string,
  board: any
): any {
  if (!board || typeof board !== 'object') return board;
  if (!Array.isArray(board.blocks)) return board;

  const noAnswers = profile.noAnswerPhases.includes(phase);

  // Force meta.mode = 'question' if profile requires it
  const meta = { ...(board.meta || {}) };
  if (profile.forceQuestionMeta && noAnswers) {
    meta.mode = 'question';
  }

  let blocks = [...board.blocks];

  if (noAnswers) {
    const allowed = new Set(profile.allowedBlockTypesInNoAnswerPhases);
    blocks = blocks.filter((b: any) => allowed.has(String(b?.type || '')));

    // Best-effort sanitization: remove obvious answer-ish fields
    blocks = blocks.map((b: any) => {
      if (!b || typeof b !== 'object') return b;
      const copy: any = { ...b };
      for (const k of ['answer', 'modelAnswer', 'explanation', 'correct', 'solution']) {
        if (k in copy) delete copy[k];
      }
      return copy;
    });
  }

  return { ...board, meta, blocks };
}

// ── Board source routing ─────────────────────────────────

/**
 * Should we call digiboard.php for this turn?
 * Returns false if the profile is avatar-driven (board comes from GPT response).
 */
export function shouldFetchExternalBoard(profile: GameProfile): boolean {
  return profile.boardSource === 'inferred';
}

// ── Safe board parser ────────────────────────────────────

/**
 * Safely parse a board payload from the avatar's response.
 * Returns null if parsing fails (frontend falls back gracefully).
 */
export function safeParseBoard(raw: unknown): BoardPayload | null {
  if (!raw || typeof raw !== 'object') return null;

  const obj = raw as Record<string, unknown>;

  if (typeof obj.layout !== 'string') return null;
  if (!Array.isArray(obj.blocks)) return null;

  return {
    layout: obj.layout,
    blocks: obj.blocks.map((b: any) => ({
      type: String(b?.type || 'unknown'),
      content: (b?.content && typeof b.content === 'object') ? b.content : {},
    })),
    phase: typeof obj.phase === 'string' ? obj.phase : '',
  };
}

// ── Board layout resolver ────────────────────────────────

/**
 * Get the expected layout name for the current phase.
 */
export function getLayoutForPhase(profile: GameProfile, phase: string): string {
  return profile.layoutByPhase[phase] || 'explanation';
}

// ── Control prompt builder ───────────────────────────────

/**
 * Build the control prompt for injection into the system prompt.
 * Delegates to the profile's own logic.
 */
export function buildControlPrompt(
  profile: GameProfile,
  state: GameState,
  userText: string
): string {
  return profile.buildControlPrompt(state, userText);
}

/**
 * Build the board instruction for injection into the system prompt.
 * Only produces content for avatar-driven profiles.
 */
export function buildBoardInstruction(
  profile: GameProfile,
  state: GameState
): string {
  if (profile.boardSource !== 'avatar-driven') return '';
  return profile.buildBoardInstruction(state);
}
